import React from 'react';
import { Box, Container, Typography, styled, Stack, Chip, Paper, Avatar } from '@mui/material';
import Grid from '@mui/material/Grid';
import { motion } from 'framer-motion';

// Icons
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import SettingsInputComponentIcon from '@mui/icons-material/SettingsInputComponent';
import ExtensionIcon from '@mui/icons-material/Extension';
import SensorsIcon from '@mui/icons-material/Sensors';
import MemoryIcon from '@mui/icons-material/Memory';
import SpeedIcon from '@mui/icons-material/Speed';
import SecurityIcon from '@mui/icons-material/Security';
import BluetoothConnectedIcon from '@mui/icons-material/BluetoothConnected';
import TerminalIcon from '@mui/icons-material/Terminal';

const DetailSection = styled(Box)({
  padding: '100px 0',
  background: '#030712',
  color: '#fff',
  borderTop: '1px solid rgba(255, 255, 255, 0.05)',
  position: 'relative',
  overflow: 'hidden',
});

const GlassCard = styled(Paper)(({ bordercolor }) => ({
  background: 'rgba(15, 23, 42, 0.65)',
  backdropFilter: 'blur(16px)',
  borderRadius: '20px',
  border: '1px solid rgba(255, 255, 255, 0.08)',
  padding: '24px',
  transition: 'all 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    borderColor: bordercolor || 'rgba(45, 212, 191, 0.4)',
    transform: 'translateY(-4px)',
    boxShadow: `0 12px 30px -10px ${bordercolor || 'rgba(45, 212, 191, 0.2)'}`,
  }
}));

const TechBadge = styled(Chip)({
  backgroundColor: 'rgba(45, 212, 191, 0.08)',
  color: '#2dd4bf',
  border: '1px solid rgba(45, 212, 191, 0.25)',
  fontWeight: 700,
  fontSize: '0.725rem',
  letterSpacing: 1.5,
  marginBottom: '20px',
  textTransform: 'uppercase',
  padding: '4px 8px',
});

const FeaturePoint = ({ text, subtext }) => (
  <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ mb: 2 }}>
    <Avatar 
      sx={{ 
        width: 22, 
        height: 22, 
        bgcolor: 'rgba(45, 212, 191, 0.15)', 
        color: '#2dd4bf',
        mt: 0.2 
      }}
    >
      <CheckCircleOutlineIcon sx={{ fontSize: '0.95rem' }} />
    </Avatar>
    <Box>
      <Typography variant="body2" sx={{ color: '#f1f5f9', fontWeight: 600, fontSize: '0.9rem', lineHeight: 1.4 }}>
        {text}
      </Typography>
      {subtext && (
        <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.775rem', display: 'block', mt: 0.3 }}>
          {subtext}
        </Typography>
      )}
    </Box>
  </Stack>
);

const AndroidDetail = () => {
  return (
    <DetailSection id="android-detail">
      {/* Background Ambient Glows */}
      <Box sx={{
        position: 'absolute', top: '20%', left: '-10%',
        width: '500px', height: '500px',
        background: 'radial-gradient(circle, rgba(45, 212, 191, 0.08) 0%, transparent 70%)',
        filter: 'blur(90px)', pointerEvents: 'none'
      }} />
      
      <Box sx={{
        position: 'absolute', bottom: '10%', right: '-5%',
        width: '450px', height: '450px',
        background: 'radial-gradient(circle, rgba(59, 130, 246, 0.08) 0%, transparent 70%)',
        filter: 'blur(90px)', pointerEvents: 'none'
      }} />

      <Container maxWidth="lg">
        <Grid container spacing={{ xs: 6, md: 8 }} alignItems="center">
          
          {/* ================= LEFT SIDE: VISUAL MOCKUP & HUD ================= */}
          <Grid size={{ xs: 12, md: 6 }}>
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, ease: 'easeOut' }}
            >
              <Box sx={{ position: 'relative' }}>
                
                {/* Main Graphic Container */}
                <Box
                  sx={{
                    position: 'relative',
                    borderRadius: '28px',
                    overflow: 'hidden',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    boxShadow: '0 25px 60px -15px rgba(0, 0, 0, 0.8)',
                    bgcolor: 'rgba(15, 23, 42, 0.8)',
                  }}
                >
                  <Box
                    component="img"
                    src="https://images.unsplash.com/photo-1511707171634-5f897ff02aa9?auto=format&fit=crop&q=80&w=1000"
                    alt="Advanced Android Hardware Architecture"
                    sx={{
                      width: '100%',
                      height: { xs: '360px', sm: '480px' },
                      objectFit: 'cover',
                      filter: 'brightness(0.7) contrast(1.15)',
                      display: 'block'
                    }}
                  />
                  <Box sx={{
                    position: 'absolute', inset: 0,
                    background: 'linear-gradient(180deg, rgba(3, 7, 18, 0.2) 0%, rgba(3, 7, 18, 0.85) 100%)'
                  }} />
                </Box>

                {/* Floating HUD Card 1: BLE Hardware Sync */}
                <Paper
                  component={motion.div}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.3, duration: 0.6 }}
                  sx={{
                    position: 'absolute',
                    top: { xs: '16px', sm: '24px' },
                    left: { xs: '16px', sm: '24px' },
                    p: 2,
                    borderRadius: '16px',
                    bgcolor: 'rgba(15, 23, 42, 0.85)',
                    border: '1px solid rgba(45, 212, 191, 0.3)',
                    backdropFilter: 'blur(12px)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    boxShadow: '0 10px 30px rgba(0,0,0,0.5)',
                    zIndex: 2
                  }}
                >
                  <Avatar sx={{ bgcolor: 'rgba(45, 212, 191, 0.15)', color: '#2dd4bf', width: 40, height: 40 }}>
                    <BluetoothConnectedIcon sx={{ fontSize: '1.25rem' }} />
                  </Avatar>
                  <Box>
                    <Typography variant="caption" sx={{ color: '#2dd4bf', fontWeight: 700, letterSpacing: 0.5, display: 'block' }}>
                      BLE PERIPHERAL
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#fff', fontWeight: 600, fontSize: '0.85rem' }}>
                      Low Latency Scanning (99.8%)
                    </Typography>
                  </Box>
                </Paper>

                {/* Floating HUD Card 2: Zebra RFID / Hardware Status */}
                <Paper
                  component={motion.div}
                  initial={{ opacity: 0, y: -20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.5, duration: 0.6 }}
                  sx={{
                    position: 'absolute',
                    bottom: { xs: '16px', sm: '24px' },
                    right: { xs: '16px', sm: '24px' },
                    p: 2,
                    borderRadius: '16px',
                    bgcolor: 'rgba(15, 23, 42, 0.85)',
                    border: '1px solid rgba(59, 130, 246, 0.3)',
                    backdropFilter: 'blur(12px)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    boxShadow: '0 10px 30px rgba(0,0,0,0.5)',
                    zIndex: 2
                  }}
                >
                  <Avatar sx={{ bgcolor: 'rgba(59, 130, 246, 0.15)', color: '#60a5fa', width: 40, height: 40 }}>
                    <SensorsIcon sx={{ fontSize: '1.25rem' }} />
                  </Avatar>
                  <Box>
                    <Typography variant="caption" sx={{ color: '#60a5fa', fontWeight: 700, letterSpacing: 0.5, display: 'block' }}>
                      RFID / SDK ENGINE
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#fff', fontWeight: 600, fontSize: '0.85rem' }}>
                      Zebra & Honeywell Ready
                    </Typography>
                  </Box>
                </Paper>

              </Box>
            </motion.div>
          </Grid>

          {/* ================= RIGHT SIDE: TECHNICAL ARCHITECTURE ================= */}
          <Grid size={{ xs: 12, md: 6 }}>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
            >
              <TechBadge label="NATIVE SYSTEM ARCHITECTURE" />

              <Typography 
                variant="h2" 
                sx={{ 
                  fontWeight: 800, 
                  mb: 2.5, 
                  fontSize: { xs: '2rem', sm: '2.5rem', md: '2.8rem' }, 
                  lineHeight: 1.15,
                  letterSpacing: '-0.02em',
                  color: '#f8fafc'
                }}
              >
                Enterprise-Grade <br />
                <Box 
                  component="span" 
                  sx={{ 
                    background: 'linear-gradient(135deg, #2dd4bf 0%, #3b82f6 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    display: 'inline-block'
                  }}
                >
                  Hardware Integration
                </Box>
              </Typography>

              <Typography variant="body1" sx={{ color: '#94a3b8', mb: 4, fontSize: '1rem', lineHeight: 1.7, fontWeight: 400 }}>
                Developing high-reliability native Android applications capable of direct hardware communication. Engineering low-level protocols for industrial barcode scanners, <b>RFID/NFC</b> data streams, <b>BLE</b> peripheral pairing, and custom <b>C++/NDK</b> native modules.
              </Typography>

              {/* Grid Cards for Architecture Categories */}
              <Grid container spacing={2.5} sx={{ mb: 4 }}>
                
                {/* Card 1: Hardware & Connectivity */}
                <Grid size={{ xs: 12, sm: 6 }}>
                  <GlassCard bordercolor="rgba(59, 130, 246, 0.4)">
                    <Typography 
                      variant="subtitle1" 
                      sx={{ 
                        color: '#f8fafc', 
                        mb: 2, 
                        fontWeight: 700, 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: 1.2,
                        fontSize: '1rem'
                      }}
                    >
                      <SensorsIcon sx={{ fontSize: '1.2rem', color: '#60a5fa' }} /> Hardware & Connectivity
                    </Typography>

                    <FeaturePoint text="RFID & NFC Protocols" subtext="High-frequency tag read/write handling" />
                    <FeaturePoint text="Bluetooth Low Energy (BLE)" subtext="Background peripheral auto-reconnect" />
                    <FeaturePoint text="Serial Port & USB I/O" subtext="Direct POS & peripheral communication" />
                  </GlassCard>
                </Grid>

                {/* Card 2: SDKs & Intelligence */}
                <Grid size={{ xs: 12, sm: 6 }}>
                  <GlassCard bordercolor="rgba(45, 212, 191, 0.4)">
                    <Typography 
                      variant="subtitle1" 
                      sx={{ 
                        color: '#f8fafc', 
                        mb: 2, 
                        fontWeight: 700, 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: 1.2,
                        fontSize: '1rem'
                      }}
                    >
                      <ExtensionIcon sx={{ fontSize: '1.2rem', color: '#2dd4bf' }} /> SDKs & Intelligence
                    </Typography>

                    <FeaturePoint text="Zebra & Scanner SDKs" subtext="Industrial warehouse automation" />
                    <FeaturePoint text="Google ML Kit & Vision" subtext="Real-time OCR & document parsing" />
                    <FeaturePoint text="Jetpack Compose Architecture" subtext="Clean MVVM/MVI reactive state" />
                  </GlassCard>
                </Grid>

              </Grid>

              {/* Firebase & Real-time Systems Banner */}
              <Paper
                elevation={0}
                sx={{ 
                  p: 3, 
                  background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.9) 0%, rgba(30, 41, 59, 0.5) 100%)', 
                  borderRadius: '20px', 
                  border: '1px solid rgba(255, 255, 255, 0.08)',
                  backdropFilter: 'blur(12px)',
                  display: 'flex', 
                  flexDirection: { xs: 'column', sm: 'row' },
                  alignItems: { xs: 'flex-start', sm: 'center' }, 
                  justifyContent: 'space-between',
                  gap: 2
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Avatar sx={{ bgcolor: 'rgba(255, 160, 0, 0.15)', color: '#ffa000', width: 44, height: 44 }}>
                    <LocalFireDepartmentIcon sx={{ fontSize: '1.4rem' }} />
                  </Avatar>
                  <Box>
                    <Typography variant="subtitle2" sx={{ color: '#f8fafc', fontWeight: 700, fontSize: '0.975rem' }}>
                      Full Firebase Cloud Infrastructure
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#94a3b8', display: 'block', mt: 0.3 }}>
                      Real-time Sync, Cloud Firestore, FCM Push Notifications & Offline Caching.
                    </Typography>
                  </Box>
                </Box>

                <Stack direction="row" spacing={1} sx={{ mt: { xs: 1, sm: 0 } }}>
                  <Chip 
                    label="FMS" 
                    size="small" 
                    sx={{ bgcolor: 'rgba(255, 255, 255, 0.05)', color: '#cbd5e1', fontSize: '0.7rem', fontWeight: 600 }} 
                  />
                  <Chip 
                    label="Logistics" 
                    size="small" 
                    sx={{ bgcolor: 'rgba(255, 255, 255, 0.05)', color: '#cbd5e1', fontSize: '0.7rem', fontWeight: 600 }} 
                  />
                  <Chip 
                    label="Inventory" 
                    size="small" 
                    sx={{ bgcolor: 'rgba(255, 255, 255, 0.05)', color: '#cbd5e1', fontSize: '0.7rem', fontWeight: 600 }} 
                  />
                </Stack>
              </Paper>

            </motion.div>
          </Grid>

        </Grid>
      </Container>
    </DetailSection>
  );
};

export default AndroidDetail;